import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Design system
import type { SelectOptionPropsType } from '@Shelly/shelly-design-system/index';
//Helpers
import { devices_settings } from 'src/stores/device-runtime/status';
import { capFirstChar } from 'src/helpers/utilities';
import {
	getCameraZone,
	getCameraZonesList,
	getZoneId,
} from 'src/components/Camera/helpers/camera-zone-helpers';
//Types
import type { Device } from 'src/types/Device';
import type { ActionJob, LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';
import type { CameraZoneType } from 'src/components/Camera/types/camera-zones';

export type CameraLocalAction = 'capture' | 'record' | 'privacy' | 'arm' | 'zone';
export type CameraLocalActionZone = {
	id: string;
	title: string;
	type: CameraZoneType;
	icon: string;
};

export const CAMERA_DURATION_MIN = 1;
export const CAMERA_DURATION_MAX = 3600;
export const CAMERA_DURATION_DEFAULT = 30;

export const CAMERA_LOCAL_ACTION_METHODS: Record<CameraLocalAction, string> = {
	record: 'camera.startrecording',
	capture: 'camera.captureimage',
	privacy: 'camera.setconfig',
	arm: 'camera.setconfig',
	zone: 'camerazone.setconfig',
};

export const getCameraLocalActionCards = (showZone = false) => {
	const $format = get(format);
	const cards = [
		{
			id: 'record' as CameraLocalAction,
			title: $format('local_action_camera_start_recording', { default: 'Start Recording' }),
			icon: 'fas fa-circle-dot',
		},
		{
			id: 'capture' as CameraLocalAction,
			title: $format('local_action_camera_capture_image', { default: 'Capture Image' }),
			icon: 'fas fa-camera',
		},
		{
			id: 'privacy' as CameraLocalAction,
			title: $format('camera_privacy_mode', { default: 'Privacy Mode' }),
			icon: 'fas fa-video-slash',
		},
		{
			id: 'arm' as CameraLocalAction,
			title: $format('camera_arm_disarm', { default: 'Arm / Disarm' }),
			icon: 'fas fa-shield',
		},
	];

	if (showZone) {
		cards.push({
			id: 'zone' as CameraLocalAction,
			title: $format('scene_camera_action_zone', { default: 'Camera zone' }),
			icon: 'fas fa-vector-square',
		});
	}

	return cards;
};

export const getCameraStateCards = (method: CameraLocalAction) => {
	const $format = get(format);
	switch (method) {
		case 'privacy':
			return [
				{
					value: true,
					title: $format('camera_privacy_mode_on', { default: 'Privacy On' }),
					icon: 'fas fa-video-slash',
				},
				{
					value: false,
					title: $format('camera_privacy_mode_off', { default: 'Privacy Off' }),
					icon: 'fas fa-video',
				},
			];
		case 'arm':
			return [
				{
					value: true,
					title: $format('camera_motion_detection_armed', { default: 'Armed' }),
					icon: 'fas fa-shield',
				},
				{
					value: false,
					title: $format('camera_motion_detection_disarmed', { default: 'Disarmed' }),
					icon: 'fas fa-shield-halved',
				},
			];
		case 'zone':
			return [
				{
					value: true,
					title: $format('camera_zone_enable_action', { default: 'Enable zone' }),
					icon: 'fas fa-circle-check',
				},
				{
					value: false,
					title: $format('camera_zone_disable_action', { default: 'Disable zone' }),
					icon: 'fas fa-circle-xmark',
				},
			];
		default:
			return [];
	}
};

export const isCameraConfigAction = (action: CameraLocalAction): boolean =>
	CAMERA_LOCAL_ACTION_METHODS[action] === 'camera.setconfig';

export const getCameraConfigActionTitle = (action: CameraLocalAction): string => {
	const $format = get(format);

	switch (action) {
		case 'privacy':
			return $format('scene_camera_select_privacy', { default: 'Set privacy mode' });
		case 'arm':
			return $format('scene_camera_select_arm', { default: 'Arm or disarm camera' });
		default:
			return '';
	}
};

export const getSelectedCameraAction = (method: string): CameraLocalAction => {
	return (Object.entries(CAMERA_LOCAL_ACTION_METHODS) as [CameraLocalAction, string][]).find(
		([, value]) => value === method,
	)?.[0];
};

export const isCameraLocalActionMethod = (method: string): boolean => {
	return /^(camera\.(captureimage|startrecording|setconfig)|camerazone\.setconfig)$/i.test(
		method?.split(':')[0] || '',
	);
};

const getCameraZoneTypeIcon = (type: CameraZoneType): string =>
	type === 'privacy' ? 'fas fa-video-slash' : 'fas fa-vector-square';

export const getCameraLocalActionZones = (device: Device): CameraLocalActionZone[] => {
	const deviceSettings = devices_settings.getByRealId(device.id);
	const $format = get(format);
	if (!deviceSettings) {
		return [];
	}

	return getCameraZonesList(deviceSettings)
		.map((componentKey: string) => {
			const zoneId = getZoneId(componentKey);
			const zone = getCameraZone(deviceSettings, zoneId);
			const type = zone?.type ?? 'motion';
			return {
				id: String(zoneId),
				title:
					zone?.name ||
					$format('camera_zone', {
						values: { id: zoneId },
						default: `Zone ${zoneId}`,
					}),
				type,
				icon: getCameraZoneTypeIcon(type),
			};
		})
		.sort((a, b) => Number(a.id) - Number(b.id));
};

export const getCameraLocalActions = (
	action: ActionJob,
	device: Device,
	params: { id: number },
): LocalControlOptions[] => {
	const $format = get(format);
	const target = getTargetOptions(device);
	const zones = getCameraLocalActionZones(device);
	const isZoneAction = action.method?.toLowerCase() === 'camerazone.setconfig';

	const actions: LocalControlOptions[] = [
		{
			title: $format('local_action_camera_start_recording', {
				default: 'Start Recording',
			}),
			value: 'camera.startrecording',
			attr: {
				target,
				duration: action.params?.duration != null,
			},
			params: {
				...params,
				duration: action.params?.duration ?? null,
			},
		},
		{
			title: $format('local_action_camera_capture_image', {
				default: 'Capture Image',
			}),
			value: 'camera.captureimage',
			attr: { target },
			params,
		},
		{
			title: $format('local_action_camera_configure', {
				default: 'Configure Camera',
			}),
			value: 'camera.setconfig',
			attr: {
				target,
				config: {
					arm: action.params?.config?.arm != null,
					privacy: action.params?.config?.privacy != null,
				},
			},
			params: {
				...params,
				config: {
					arm: action.params?.config?.arm || false,
					privacy: action.params?.config?.privacy || false,
				},
			},
		},
	];

	if (zones.length > 0) {
		actions.push({
			title: $format('scene_camera_action_zone', {
				default: 'Camera zone',
			}),
			value: 'camerazone.setconfig',
			attr: {
				config: {
					enable: isZoneAction && action.params?.config?.enable != null,
				},
			},
			params: {
				...(isZoneAction && action.params?.id != null ? { id: action.params.id } : {}),
				config: {
					enable: action.params?.config?.enable ?? false,
				},
			},
		});
	}

	return actions;
};

const getTargetOptions = (device: Device): SelectOptionPropsType[] => {
	const $format = get(format);
	const channelsOptions: SelectOptionPropsType[] = [];
	const deviceSettings = devices_settings.getByRealId(device.id) || {};
	Object.keys(deviceSettings).forEach((key) => {
		if (key.startsWith('camera:')) {
			const cid = deviceSettings[key]?.id ?? 0;
			channelsOptions.push({
				title:
					deviceSettings[key]?.name ||
					$format('webhooks_component_camera', {
						values: { id: cid },
						default: `Camera (${cid})`,
					}),
				value: cid,
			});
		}
	});
	return channelsOptions;
};

export const getCameraComponents = (deviceSetting: any) => {
	const options = [];
	const components = Object.keys(deviceSetting || {})
		.filter((i) => /^(camera|camerazone):\d/.test(i))
		.sort();
	for (let i = 0; i < components.length; ++i) {
		const type = components[i].split(':')[0];
		const component = deviceSetting[components[i]];
		const cid = component.id ?? Number(components[i].split(':')[1]) ?? 0;
		options.push({
			title: capFirstChar(component.name || `${type} (${component.id})`),
			type,
			component: components[i],
			inputType: type,
			cid,
		});
	}
	return options;
};
